// useReportManagement.js
import { useState, useEffect, useCallback, useRef } from 'react';
import { useSocket } from '../context/SocketContext';
import useNotification from '../components/Notifications/useNotifications';
import { reportService } from '../services';
import { useAuth } from '../context/AuthContext';
import { Report } from '../models';
import {
  mergeReports,
  purgeOldReports,
  saveReportsToLocalStorage,
  loadReportsFromLocalStorage,
  saveLastRefreshDateToLocalStorage,
  loadLastRefreshDateFromLocalStorage,
  getLatestReportDate,
  extractUniqueReportTypes,
  normalizeReport
} from './reportListUtils';
import { LIMIT_PER_PAGE } from './reportListConstants';
import { createLogger } from '../services/Debug';
export function useReportManagement() {
  const { profile } = useAuth();
  const log = createLogger('useReportManagement');
  const [reports, setReports] = useState([]);
  const [lastReportDate, setLastReportDate] = useState(null);
  const [hasMore, setHasMore] = useState(true);
  const [loading, setLoading] = useState(false);
  const [loadingText, setLoadingText] = useState("Chargement des rapports...");
  const [reportTypes, setReportTypes] = useState([]);
  const [extractedReportTypes, setExtractedReportTypes] = useState([]);
  const [refreshCountdown, setRefreshCountdown] = useState(0);
  
  const lastReportDateRef = useRef(lastReportDate);
  const isRefreshingRef = useRef(false);
  
  const { showNotification } = useNotification();
  const { 
    hasNewReports,
    newReports: socketReports,
    getNewReportsAndClear,
    clearReports: clearSocketReports
  } = useSocket();

  // Extract report types from reports
  const extractReportTypes = useCallback((arr) => {
    const uniqueTypes = extractUniqueReportTypes(arr);
    setExtractedReportTypes(uniqueTypes);
  }, []);

  // Sync last refresh date
  const syncLastRefreshDate = useCallback(() => {
    if (reports.length > 0) {
      const latestDate = getLatestReportDate(reports);
      const storedDate = loadLastRefreshDateFromLocalStorage();
      
      if (!storedDate || latestDate > storedDate) {
        setLastReportDate(latestDate);
        saveLastRefreshDateToLocalStorage(latestDate.toISOString());
        lastReportDateRef.current = latestDate;
      }
    }
  }, [reports]);

  // Initialize reports on mount
  useEffect(() => {
    const initializeReports = async () => {
      setLoading(true);
      setLoadingText("Chargement des rapports...");
      
      const minimumLoadingTime = 500;
      const startTime = Date.now();
      
      try {
        const stored = loadReportsFromLocalStorage();
        const storedLastRefresh = loadLastRefreshDateFromLocalStorage();
        
        if (stored.length > 0 && storedLastRefresh) {
          // Use cached data immediately
          setReports(stored);
          setLastReportDate(storedLastRefresh);
          extractReportTypes(stored);
          
          // Refresh in background
          const refreshResponse = await reportService.refreshReports(storedLastRefresh);
          if (refreshResponse.reports && refreshResponse.reports.length > 0) {
            const newReports = refreshResponse.reports;
            
            // Handle deleted reports if any
            let existingReports = stored;
            if (refreshResponse.deletedIds && refreshResponse.deletedIds.length > 0) {
              existingReports = stored.filter(r => !refreshResponse.deletedIds.includes(r.id));
            }
            
            const combined = mergeReports(existingReports, newReports);
            const purged = purgeOldReports(combined);
            
            setReports(purged);
            saveReportsToLocalStorage(purged);
            
            // Use syncDate from response
            const syncDate = refreshResponse.syncDate;
            setLastReportDate(syncDate);
            saveLastRefreshDateToLocalStorage(syncDate.toISOString());
            
            extractReportTypes(purged);
            
            // Ensure minimum time has passed
            const elapsedTime = Date.now() - startTime;
            const remainingTime = Math.max(0, minimumLoadingTime - elapsedTime);
            
            if (remainingTime > 0) {
              await new Promise(resolve => setTimeout(resolve, remainingTime));
            }
            
            showNotification(`${newReports.length} nouveau(x) rapport(s)`, 'success');
          }
        } else {
          // No cached data, fetch initial reports
          const initialResponse = await reportService.getInitialReports();
          
          if (initialResponse.reports && initialResponse.reports.length > 0) {
            setReports(initialResponse.reports);
            saveReportsToLocalStorage(initialResponse.reports);
            
            // Use syncDate from response
            const syncDate = initialResponse.syncDate;
            setLastReportDate(syncDate);
            saveLastRefreshDateToLocalStorage(syncDate.toISOString());
            
            // Check if there might be more reports based on stats
            const stats = initialResponse.stats;
            setHasMore(stats && stats.totalReports > initialResponse.reports.length);
            
            extractReportTypes(initialResponse.reports);
          } else {
            setHasMore(false);
          }
          
          // Ensure minimum time has passed for initial load
          const elapsedTime = Date.now() - startTime;
          const remainingTime = Math.max(0, minimumLoadingTime - elapsedTime);
          
          if (remainingTime > 0) {
            await new Promise(resolve => setTimeout(resolve, remainingTime));
          }
        }
        
        
        if (profile) {
          setReportTypes(profile.reportTypeFavorites.getFavorites());
        }
      } catch (error) {
        log.error('Error initializing reports:', error);
        
        // Even on error, respect minimum loading time
        const elapsedTime = Date.now() - startTime;
        const remainingTime = Math.max(0, minimumLoadingTime - elapsedTime);
        
        if (remainingTime > 0) {
          await new Promise(resolve => setTimeout(resolve, remainingTime));
        }
        
        showNotification('Erreur lors du chargement des rapports', 'error');
      } finally {
        setLoading(false);
      }
    };

    initializeReports();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Handle refresh
  const handleRefresh = useCallback(async () => {
    if (isRefreshingRef.current) {
      return;
    }
    isRefreshingRef.current = true;
    setRefreshCountdown(2);
    
    const minimumLoadingTime = 500;
    const startTime = Date.now();
    
    let dateToUse = lastReportDateRef.current;
    
    if (reports.length > 0) {
      const latestInMemory = getLatestReportDate(reports);
      if (!dateToUse || latestInMemory > dateToUse) {
        dateToUse = latestInMemory;
      }
    }
    
    if (!dateToUse) {
      const stored = loadLastRefreshDateFromLocalStorage();
      if (stored) {
        setLastReportDate(stored);
        dateToUse = stored;
      } else {
        isRefreshingRef.current = false;
        return;
      }
    }
    
    setLoading(true);
    setLoadingText("Actualisation des rapports...");
    
    try {
      const refreshResponse = await reportService.refreshReports(dateToUse);
      
      if (refreshResponse.reports && refreshResponse.reports.length > 0) {
        const newReports = refreshResponse.reports;
        let localExisting = loadReportsFromLocalStorage();
        
        // Handle deleted reports
        if (refreshResponse.deletedIds && refreshResponse.deletedIds.length > 0) {
          localExisting = localExisting.filter(r => !refreshResponse.deletedIds.includes(r.id));
        }
        
        let combined = mergeReports(localExisting, newReports);
        combined = purgeOldReports(combined);
        
        setReports(combined);
        saveReportsToLocalStorage(combined);
        
        // Use syncDate from response
        const syncDate = refreshResponse.syncDate;
        setLastReportDate(syncDate);
        saveLastRefreshDateToLocalStorage(syncDate.toISOString());
        lastReportDateRef.current = syncDate;
        
        setHasMore(newReports.length >= LIMIT_PER_PAGE);
        
        // Ensure minimum loading time has passed
        const elapsedTime = Date.now() - startTime;
        const remainingTime = Math.max(0, minimumLoadingTime - elapsedTime);
        
        if (remainingTime > 0) {
          await new Promise(resolve => setTimeout(resolve, remainingTime));
        }
        
        showNotification(`${newReports.length} nouveau(x) rapport(s)`, 'success');
        extractReportTypes(combined);
      } else {
        // Ensure minimum loading time has passed
        const elapsedTime = Date.now() - startTime;
        const remainingTime = Math.max(0, minimumLoadingTime - elapsedTime);
        
        if (remainingTime > 0) {
          await new Promise(resolve => setTimeout(resolve, remainingTime));
        }
        
        showNotification('Vos rapports sont à jour.', 'classic');
        setHasMore(false);
      }
    } catch (error) {
      log.error('Error during refresh operation:', error);
      
      // Even on error, respect minimum loading time
      const elapsedTime = Date.now() - startTime;
      const remainingTime = Math.max(0, minimumLoadingTime - elapsedTime);
      
      if (remainingTime > 0) {
        await new Promise(resolve => setTimeout(resolve, remainingTime));
      }
      
      showNotification('Erreur lors du rafraîchissement', 'error');
    } finally {
      setLoading(false);
      isRefreshingRef.current = false;
    }
  }, [extractReportTypes, showNotification, reports]);

  // Handle socket reports
  useEffect(() => {
    if (hasNewReports && socketReports.length > 0) {
      const reportsToProcess = getNewReportsAndClear();
      
      // Convert socket reports to proper Report instances
      const reportInstances = reportsToProcess.map(data => new Report(data));
      
      setReports(prevReports => {
        const combined = mergeReports(prevReports, reportInstances);
        saveReportsToLocalStorage(combined);
        extractReportTypes(combined);
        return combined;
      });
      
      showNotification(
        `${reportsToProcess.length} nouveau${reportsToProcess.length > 1 ? 'x' : ''} rapport${reportsToProcess.length > 1 ? 's' : ''}`,
        'success'
      );
      
      clearSocketReports();
    }
  }, [hasNewReports, socketReports, getNewReportsAndClear, extractReportTypes, showNotification, clearSocketReports]);

  // Sync last refresh date when reports change
  useEffect(() => {
    syncLastRefreshDate();
  }, [reports, syncLastRefreshDate]);

  // Update last report date ref
  useEffect(() => {
    lastReportDateRef.current = lastReportDate;
  }, [lastReportDate]);

  // Countdown timer
  useEffect(() => {
    if (refreshCountdown > 0) {
      const timer = setTimeout(() => setRefreshCountdown(c => c - 1), 1000);
      return () => clearTimeout(timer);
    } else {
      isRefreshingRef.current = false;
    }
  }, [refreshCountdown]);

  // Report actions using the service
  const archiveReport = useCallback(async (reportId) => {
    try {
      await reportService.archiveReport(reportId);
      return {};
    } catch (error) {
      log.error('Error archiving report:', error);
      throw error;
    }
  }, []);

  const improveReportWithAI = useCallback(async (reportId, correctedContent, structuredData) => {
    try {
      const updatedReport = await reportService.updateReport(reportId, {
        correctedContent,
        isCorrected: true,
        structuredData
      });
      return updatedReport;
    } catch (error) {
      log.error('Error improving report with AI:', error);
      throw error;
    }
  }, []);

  const changeReportType = useCallback(async (newType, transcription, annex) => {
    try {
      const report = await reportService.generateReport(newType, transcription, annex);
      return report;
    } catch (error) {
      log.error('Error generating report with new type:', error);
      throw error;
    }
  }, []);

  return {
    reports,
    setReports,
    loading,
    loadingText,
    hasMore,
    reportTypes,
    extractedReportTypes,
    refreshCountdown,
    isRefreshingRef,
    handleRefresh,
    archiveReport,
    improveReportWithAI,
    changeReportType
  };
}